/**
 * AUTH LISTENER SERVICE - SINGLETON
 *
 * Un solo `onAuthStateChange` en la app. GoTrue suele emitir varias veces
 * `SIGNED_IN` / `INITIAL_SESSION` con el mismo `access_token`; aquí se notifica
 * solo la primera hasta `SIGNED_OUT` o cambio real de token (`TOKEN_REFRESHED`).
 */

import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';

type AuthStateChangeCallback = (event: string, session: unknown) => void;

class AuthListenerService {
  private static instance: AuthListenerService;
  private subscription: { unsubscribe: () => void } | null = null;
  private callbacks: Set<AuthStateChangeCallback> = new Set();
  private isInitialized = false;

  /**
   * Último access_token ya propagado a callbacks para eventos de sesión “activa”.
   * Evita SIGNED_IN + INITIAL_SESSION duplicados y ráfagas del cliente GoTrue.
   */
  private sessionAnnouncedAccessToken: string | null = null;

  static getInstance(): AuthListenerService {
    if (!AuthListenerService.instance) {
      AuthListenerService.instance = new AuthListenerService();
    }
    return AuthListenerService.instance;
  }

  subscribe(callback: AuthStateChangeCallback): () => void {
    this.callbacks.add(callback);

    if (!this.isInitialized) {
      this.initialize();
    }

    return () => {
      this.callbacks.delete(callback);
      // Importante: NO llamar cleanup() cuando callbacks.size === 0.
      // Desuscribirse de GoTrue aquí provoca churn (re-inicialización), repetición de
      // INITIAL_SESSION/SIGNED_IN y en dev con StrictMode parece un bucle infinito de
      // subscribe/cleanup en los componentes que registran listeners de depuración.
      // El canal de auth permanece vivo mientras viva la SPA (coste mínimo).
    };
  }

  /**
   * Solo para pruebas o entornos que necesiten liberar el cliente de auth.
   */
  destroy(): void {
    this.cleanup();
  }

  private broadcast(event: string, session: unknown) {
    logger.debug(`[AuthListenerService] Event: ${event}`, {
      userId: (session as { user?: { id?: string } } | null)?.user?.id,
      email: (session as { user?: { email?: string } } | null)?.user?.email,
    });

    this.callbacks.forEach((callback) => {
      try {
        callback(event, session);
      } catch (error) {
        logger.error('Error en callback de auth:', error);
      }
    });
  }

  private emit(event: string, session: unknown) {
    const token = (session as { access_token?: string } | null)?.access_token ?? null;

    if (event === 'SIGNED_OUT') {
      this.sessionAnnouncedAccessToken = null;
      this.broadcast(event, session);
      return;
    }

    if (event === 'INITIAL_SESSION' && !token) {
      this.sessionAnnouncedAccessToken = null;
      this.broadcast(event, session);
      return;
    }

    if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && token) {
      if (token === this.sessionAnnouncedAccessToken) {
        return;
      }
      this.sessionAnnouncedAccessToken = token;
      this.broadcast(event, session);
      return;
    }

    if (event === 'TOKEN_REFRESHED' && token) {
      if (token === this.sessionAnnouncedAccessToken) {
        return;
      }
      this.sessionAnnouncedAccessToken = token;
      this.broadcast(event, session);
      return;
    }

    this.broadcast(event, session);
  }

  private initialize() {
    if (this.subscription) {
      logger.warn('⚠️ AuthListenerService: Ya existe una suscripción activa');
      return;
    }

    const authState = supabase.auth.onAuthStateChange((event, session) => {
      this.emit(event, session);
    });

    const subscription =
      authState &&
      typeof authState === 'object' &&
      'data' in authState &&
      authState.data &&
      typeof authState.data === 'object' &&
      'subscription' in authState.data
        ? (authState.data as { subscription: { unsubscribe: () => void } }).subscription
        : null;

    if (!subscription || typeof subscription.unsubscribe !== 'function') {
      logger.error('❌ AuthListenerService: estructura inesperada de onAuthStateChange', {
        keys: authState && typeof authState === 'object' ? Object.keys(authState) : [],
      });
      return;
    }

    this.subscription = subscription;
    this.isInitialized = true;
    logger.info('✅ AuthListenerService inicializado', {
      callbacksCount: this.callbacks.size,
    });
  }

  private cleanup() {
    if (this.subscription) {
      if (typeof this.subscription.unsubscribe === 'function') {
        try {
          this.subscription.unsubscribe();
          logger.info('✅ AuthListenerService limpiado correctamente');
        } catch (error) {
          logger.error('❌ Error al hacer unsubscribe en AuthListenerService:', error);
        }
      }
      this.subscription = null;
      this.isInitialized = false;
      this.sessionAnnouncedAccessToken = null;
    }
  }

  getStats() {
    return {
      isInitialized: this.isInitialized,
      callbacksCount: this.callbacks.size,
      hasSubscription: !!this.subscription,
    };
  }
}

export const authListenerService = AuthListenerService.getInstance();

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  setInterval(() => {
    const stats = authListenerService.getStats();
    if (stats.callbacksCount > 0) {
      console.log('[AuthListenerService] Stats:', stats);
    }
  }, 60000);
}
